import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  CalendarClock,
  CheckCircle2,
  Loader2,
  Plus,
  Save,
  Trash2,
  Play,
} from 'lucide-react';
import {
  harvesterSchedulesApi,
  type HarvesterSchedule,
} from '@/services/api';

const HARVESTER_TYPES: Array<{
  value: HarvesterSchedule['harvester_type'];
  label: string;
}> = [
  { value: 'customer_research', label: 'Customer Research' },
  { value: 'market_taxonomy', label: 'Market Taxonomy' },
  { value: 'market_persona', label: 'Market Persona' },
  { value: 'brand_design', label: 'Brand Design' },
];

const CRON_PRESETS: Array<{ label: string; expression: string }> = [
  { label: 'Daily at 2am', expression: '0 2 * * *' },
  { label: 'Weekly Mon 3am', expression: '0 3 * * 1' },
  { label: 'Twice weekly (Mon+Thu)', expression: '0 3 * * 1,4' },
  { label: 'Monthly 1st at 3am', expression: '0 3 1 * *' },
  { label: 'Custom', expression: '' },
];

function describeCron(cron: string): string {
  // Lightweight preview — not a full parser, just covers common cases.
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return 'Invalid cron';
  const [min, hour, dom, mon, dow] = parts;
  if (min === '0' && hour.match(/^\d+$/) && dom === '*' && mon === '*') {
    if (dow === '*') return `Daily at ${hour}:00`;
    if (dow.match(/^[0-9,]+$/))
      return `Weekly on ${dow.split(',').map(dayName).join(', ')} at ${hour}:00`;
  }
  if (min === '0' && hour.match(/^\d+$/) && dom.match(/^\d+$/) && mon === '*' && dow === '*') {
    return `Monthly on day ${dom} at ${hour}:00`;
  }
  return cron;
}

function dayName(d: string): string {
  return (
    {
      '0': 'Sun',
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
    }[d] ?? d
  );
}

function newScheduleSkeleton(): Omit<HarvesterSchedule, 'updated_at'> {
  return {
    id: crypto.randomUUID(),
    harvester_type: 'customer_research',
    cron_expression: '0 2 * * *',
    enabled: true,
    config: {},
    budget_limit: null,
    last_triggered_at: null,
    next_scheduled_at: null,
    cloud_scheduler_job_name: null,
  };
}

export function HarvesterScheduler({ clientId }: { clientId: string }) {
  const [schedules, setSchedules] = useState<HarvesterSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    harvesterSchedulesApi
      .get(clientId)
      .then((res) => setSchedules(res.schedules))
      .catch(() => setSchedules([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (!clientId) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock size={16} className="text-slate-400" />
          <h2 className="font-semibold text-navy-900 text-sm">Harvester Schedules</h2>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          Select a client to configure harvester schedules.
        </p>
      </div>
    );
  }

  const handleChange = (
    id: string,
    updates: Partial<HarvesterSchedule>,
  ) => {
    setSchedules((s) =>
      s.map((sch) => (sch.id === id ? { ...sch, ...updates } : sch)),
    );
    setDirty(true);
  };

  const handleRemove = (id: string) => {
    setSchedules((s) => s.filter((sch) => sch.id !== id));
    setDirty(true);
  };

  const handleAdd = () => {
    setSchedules((s) => [...s, newScheduleSkeleton()]);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await harvesterSchedulesApi.put(
        clientId,
        schedules.map(
          ({
            id,
            harvester_type,
            cron_expression,
            enabled,
            config,
            budget_limit,
            last_triggered_at,
            next_scheduled_at,
            cloud_scheduler_job_name,
          }) => ({
            id,
            harvester_type,
            cron_expression,
            enabled,
            config,
            budget_limit,
            last_triggered_at,
            next_scheduled_at,
            cloud_scheduler_job_name,
          }),
        ),
      );
      setSchedules(res.schedules);
      setDirty(false);
      setStatus(`Saved ${res.schedules.length} schedule${res.schedules.length === 1 ? '' : 's'}.`);
    } catch (err) {
      setStatus(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async (id: string) => {
    setTriggeringId(id);
    try {
      const res = await harvesterSchedulesApi.trigger(clientId, id);
      setSchedules((s) =>
        s.map((sch) =>
          sch.id === id ? { ...sch, last_triggered_at: res.triggered_at } : sch,
        ),
      );
      setStatus(
        'Trigger recorded. The admin UI should now call the corresponding seed endpoint directly; this action only stamps the audit trail.',
      );
    } catch (err) {
      setStatus(`Trigger failed: ${(err as Error).message}`);
    } finally {
      setTriggeringId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            Harvester Schedules
          </h2>
          <span className="text-[10px] text-slate-400 font-mono">
            {schedules.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <Plus size={11} />
            Add schedule
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg',
              dirty
                ? 'bg-brand-teal text-navy-900 hover:bg-brand-teal/90'
                : 'bg-slate-100 text-slate-400',
            )}
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            Save
          </button>
        </div>
      </div>

      {status && (
        <div className="text-xs bg-brand-teal/5 border border-brand-teal/10 rounded-lg px-3 py-2 text-brand-teal-dark">
          {status}
        </div>
      )}

      {loading && schedules.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6">
          <Loader2 size={14} className="animate-spin inline mr-2" />
          Loading schedules...
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6">
          No schedules configured. Click <strong>Add schedule</strong> to start.
        </div>
      ) : (
        <div className="space-y-2">
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              triggering={triggeringId === s.id}
              onChange={(u) => handleChange(s.id, u)}
              onRemove={() => handleRemove(s.id)}
              onTrigger={() => handleTrigger(s.id)}
            />
          ))}
        </div>
      )}

      <p className="text-[10px] text-slate-500 italic pt-2 border-t border-slate-100">
        Schedules are stored in <code>fleet_config.schedules[]</code>. A Cloud
        Scheduler reconciler provisions/updates the actual GCP cron jobs when
        deployed — the admin UI just owns the intent.
      </p>
    </div>
  );
}

function ScheduleRow({
  schedule,
  triggering,
  onChange,
  onRemove,
  onTrigger,
}: {
  schedule: HarvesterSchedule;
  triggering: boolean;
  onChange: (u: Partial<HarvesterSchedule>) => void;
  onRemove: () => void;
  onTrigger: () => void;
}) {
  const [preset, setPreset] = useState(() => {
    const match = CRON_PRESETS.find((p) => p.expression === schedule.cron_expression);
    return match ? match.label : 'Custom';
  });
  const description = describeCron(schedule.cron_expression);
  const lastTriggered = schedule.last_triggered_at
    ? new Date(schedule.last_triggered_at).toLocaleString()
    : '—';

  return (
    <div
      className={clsx(
        'border rounded-lg p-3 space-y-2',
        schedule.enabled ? 'border-slate-200' : 'border-slate-100 bg-slate-50/60 opacity-75',
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={schedule.harvester_type}
          onChange={(e) =>
            onChange({
              harvester_type: e.target.value as HarvesterSchedule['harvester_type'],
            })
          }
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
        >
          {HARVESTER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          value={preset}
          onChange={(e) => {
            const next = CRON_PRESETS.find((p) => p.label === e.target.value);
            setPreset(e.target.value);
            if (next && next.expression) {
              onChange({ cron_expression: next.expression });
            }
          }}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
        >
          {CRON_PRESETS.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={schedule.cron_expression}
          onChange={(e) => {
            onChange({ cron_expression: e.target.value });
            setPreset('Custom');
          }}
          className="text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 w-36"
          placeholder="0 2 * * *"
        />
        <span className="text-[10px] text-slate-500 truncate max-w-[16rem]">
          {description}
        </span>
        <label className="flex items-center gap-1 text-xs text-slate-600 ml-auto">
          <input
            type="checkbox"
            checked={schedule.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="accent-brand-teal"
          />
          Enabled
        </label>
        <button
          onClick={onTrigger}
          disabled={triggering}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-slate-700 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
          title="Stamp last_triggered_at (the UI should also call the corresponding seed endpoint directly)"
        >
          {triggering ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <Play size={10} />
          )}
          Trigger
        </button>
        <button
          onClick={onRemove}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-rose-600 border border-rose-200 rounded hover:bg-rose-50"
          title="Remove schedule"
        >
          <Trash2 size={10} />
        </button>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-slate-500">
        <span>id: <code>{schedule.id.slice(0, 8)}</code></span>
        <span>last triggered: {lastTriggered}</span>
        {schedule.cloud_scheduler_job_name && (
          <span className="flex items-center gap-1">
            <CheckCircle2 size={10} className="text-emerald-500" />
            GCP job: {schedule.cloud_scheduler_job_name}
          </span>
        )}
      </div>
    </div>
  );
}
